import { useQuery } from '@tanstack/react-query';
import { extractHeaders } from 'letterparser';
import { memo, useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import { Flex, Heading, Text, Tooltip, TooltipTrigger } from '@/components/core';
import { EmailIcon } from '@/components/icons';
import { useAgentRunTree } from '@/hooks';
import { toDocumentId, toFileId, useGetDocument, useGetFile } from '@/hooks/api';

export type AgentRunAttachmentsCardProps = {
  agentId: string;
  runId: string;
};

const IAgentRunAttachmentsCard = ({ agentId, runId }: AgentRunAttachmentsCardProps) => {
  const { emailDocument } = useAgentRunTree({ agentId, runId });
  const { data: doc } = useGetDocument(toDocumentId(emailDocument?.documentId));
  const { data: blob } = useGetFile(toFileId(doc?.fileUrl));

  const { data: emailStr } = useQuery({
    queryKey: [emailDocument?.documentId, 'text'],
    queryFn: async () => {
      if (!blob) throw Error('Blob is undefined');
      return await blob.text();
    },
  });

  const email = useMemo(() => (emailStr ? extractHeaders(emailStr) : undefined), [emailStr]);

  if (!email) return null;

  return (
    <Flex className="gap-sm group/agent-run-row flex items-center rounded-md bg-gray-100 p-2">
      <TooltipTrigger>
        <Tooltip>
          <Flex col>
            <Heading>{email.subject}</Heading>
            <Text>From:</Text>
            <Text>{email.from?.name}</Text>
            <Text>{email.from?.address}</Text>
          </Flex>
        </Tooltip>
        <Focusable>
          <EmailIcon role="button" />
        </Focusable>
      </TooltipTrigger>
    </Flex>
  );
};

export const AgentRunAttachmentsCard = memo(IAgentRunAttachmentsCard) as typeof IAgentRunAttachmentsCard;
